import React, { useState } from 'react';
import { Search, Check, X, Edit2, UserX, UserCheck, Download, Loader2 } from 'lucide-react';
import { bonificacionesAPI } from '../../../../api/bonificaciones';
import Pagination from '../../../common/Pagination';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtShort = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const PAGE_SIZE = 20;


export default function EmpleadosTab({ bonificacion, detalle, tasaInfotep = 0.005, onUpdateDetalle, editable }) {
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [editId,      setEditId]      = useState(null);
  const [editValues,  setEditValues]  = useState({});
  const [downloadingId, setDownloadingId] = useState(null);

  const esPagado = bonificacion.estado === 'pagado' && bonificacion.isr_calculado;

  const esDesempeno    = bonificacion.metodo_distribucion === 'desempeno';
  const tieneAdicional = bonificacion.tipo === 'FIJA';   // MIXTA no tiene columna adicional separada
  const tieneLegal     = bonificacion.tipo === 'LEGAL' || bonificacion.tipo === 'MIXTA';
  const esMixta        = bonificacion.tipo === 'MIXTA';
  // Para MIXTA, monto_total = tope o distribución (mutuamente excluyentes); mismo para LEGAL
  const legalLabel     = esMixta ? 'Bonif. Legal / Distrib.' : 'Bonificación Legal';

  // Peso % por empleado (todos activos)
  const todosActivos  = detalle.filter(d => d.estado !== 'excluido');
  const totalSalarios = todosActivos.reduce((s, d) => s + parseFloat(d.salario_mensual || 0), 0);

  const filtered = detalle.filter(d =>
    d.apellidos_nombres.toLowerCase().includes(search.toLowerCase()) ||
    (d.departamento || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (v) => { setSearch(v); setPage(1); };

  const startEdit = (d) => {
    setEditId(d.id);
    setEditValues({
      monto_ajustado:    d.monto_ajustado    != null ? String(parseFloat(d.monto_ajustado))    : '',
      puntaje_desempeno: d.puntaje_desempeno != null ? String(parseFloat(d.puntaje_desempeno)) : '',
    });
  };

  const cancelEdit = () => { setEditId(null); setEditValues({}); };

  const saveEdit = async (d) => {
    const payload = {};
    const ajuste = editValues.monto_ajustado.trim();
    if (ajuste !== '') payload.monto_ajustado = parseFloat(ajuste);
    else if (d.monto_ajustado != null) payload.monto_ajustado = null;
    if (esDesempeno && editValues.puntaje_desempeno.trim() !== '') {
      payload.puntaje_desempeno = parseFloat(editValues.puntaje_desempeno);
    }
    await onUpdateDetalle(d.id, payload);
    setEditId(null);
    setEditValues({});
  };

  const toggleExcluir = async (d) => {
    const nuevoEstado = d.estado === 'excluido' ? 'pendiente' : 'excluido';
    await onUpdateDetalle(d.id, { estado: nuevoEstado });
  };

  const handleDescargarComprobante = async (d) => {
    setDownloadingId(d.id);
    try {
      const blob = await bonificacionesAPI.downloadPayslip(bonificacion.id, d.id);
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `bonificacion_${d.apellidos_nombres.replace(/\s+/g, '_')}_${bonificacion.anio}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando comprobante de bonificación:', err);
      alert('Error al generar el comprobante.');
    } finally {
      setDownloadingId(null);
    }
  };

  const totalesActivos = filtered.filter(d => d.estado !== 'excluido');

  return (
    <div className="space-y-3">

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Buscar empleado o departamento…"
          className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-xs whitespace-nowrap">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">Empleado</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">Depto.</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Salario</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Antigüedad</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Meses</th>
              {esMixta && (
                <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Peso %</th>
              )}
              {tieneLegal && (
                <>
                  <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Tope</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">{legalLabel}</th>
                </>
              )}
              {tieneAdicional && (
                esDesempeno ? (
                  <>
                    <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Puntaje</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Distribución</th>
                  </>
                ) : (
                  <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Distribución</th>
                )
              )}
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Total</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">Ajuste</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider text-red-600">INFOTEP {(tasaInfotep * 100).toFixed(2)}%</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider text-orange-600">ISR a Retener</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider text-green-700">Neto</th>
              {(editable || esPagado) && <th className="px-3 py-2.5 text-center font-semibold text-gray-500 uppercase tracking-wider">Acción</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginated.map(d => {
              const excluido  = d.estado === 'excluido';
              const isEditing = editId === d.id;

              return (
                <tr key={d.id} className={`transition-colors ${excluido ? 'opacity-40 bg-gray-50' : 'hover:bg-gray-50'}`}>

                  <td className="px-3 py-2.5">
                    <p className="font-medium text-gray-900">{d.apellidos_nombres}</p>
                    {excluido && <span className="text-[10px] text-red-500 font-medium">Excluido</span>}
                  </td>

                  <td className="px-3 py-2.5 text-gray-500">{d.departamento || '—'}</td>

                  <td className="px-3 py-2.5 text-right text-gray-700">RD$ {fmtShort(d.salario_mensual)}</td>

                  <td className="px-3 py-2.5 text-right text-gray-600">
                    {parseFloat(d.anos_servicio || 0).toFixed(1)} años
                  </td>

                  <td className="px-3 py-2.5 text-right text-gray-600">{d.dias_trabajados} mes{d.dias_trabajados !== 1 ? 'es' : ''}</td>

                  {esMixta && (
                    <td className="px-3 py-2.5 text-right text-gray-500">
                      {totalSalarios > 0
                        ? ((parseFloat(d.salario_mensual || 0) / totalSalarios) * 100).toFixed(2)
                        : '0.00'}%
                    </td>
                  )}

                  {tieneLegal && (
                    <>
                      <td className="px-3 py-2.5 text-right text-gray-500">
                        {d.tope_dias}d / RD$ {fmt(d.tope_monto)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-blue-700 font-medium">
                        RD$ {fmt(esMixta ? d.monto_total : d.monto_legal)}
                      </td>
                    </>
                  )}

                  {tieneAdicional && esDesempeno && (
                    <td className="px-3 py-2.5 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues.puntaje_desempeno}
                          onChange={e => setEditValues(v => ({ ...v, puntaje_desempeno: e.target.value }))}
                          min={0} max={100} step={1}
                          className="w-16 px-2 py-1 border border-blue-300 rounded text-xs text-right focus:outline-none focus:border-blue-500"
                          placeholder="0"
                        />
                      ) : (
                        <span className={d.puntaje_desempeno ? 'text-gray-700 font-medium' : 'text-gray-300'}>
                          {d.puntaje_desempeno != null ? parseFloat(d.puntaje_desempeno).toFixed(0) : '—'}
                        </span>
                      )}
                    </td>
                  )}

                  {tieneAdicional && (
                    <td className="px-3 py-2.5 text-right text-violet-700 font-medium">
                      RD$ {fmt(d.monto_adicional)}
                    </td>
                  )}

                  <td className="px-3 py-2.5 text-right font-medium text-gray-800">
                    RD$ {fmt(d.monto_total)}
                  </td>

                  <td className="px-3 py-2.5 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editValues.monto_ajustado}
                        onChange={e => setEditValues(v => ({ ...v, monto_ajustado: e.target.value }))}
                        min={0} step={100}
                        className="w-24 px-2 py-1 border border-blue-300 rounded text-xs text-right focus:outline-none focus:border-blue-500"
                        placeholder={fmt(d.monto_total)}
                      />
                    ) : (
                      <span className={d.monto_ajustado != null ? 'text-amber-600 font-medium' : 'text-gray-300'}>
                        {d.monto_ajustado != null ? `RD$ ${fmt(d.monto_ajustado)}` : '—'}
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-2.5 text-right text-red-600 font-medium">
                    RD$ {fmt(parseFloat(d.monto_final || 0) * tasaInfotep)}
                  </td>

                  <td className="px-3 py-2.5 text-right font-medium">
                    {bonificacion.isr_calculado
                      ? <span className="text-orange-600">RD$ {fmt(d.isr_monto)}</span>
                      : <span className="text-gray-300">—</span>
                    }
                  </td>

                  <td className="px-3 py-2.5 text-right font-bold text-green-700">
                    RD$ {fmt(bonificacion.isr_calculado ? d.neto_a_pagar : parseFloat(d.monto_final || 0) * (1 - tasaInfotep))}
                  </td>

                  {(editable || esPagado) && (
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {editable && (isEditing ? (
                          <>
                            <button onClick={() => saveEdit(d)} className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors" title="Guardar">
                              <Check size={12} />
                            </button>
                            <button onClick={cancelEdit} className="p-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" title="Cancelar">
                              <X size={12} />
                            </button>
                          </>
                        ) : (
                          <>
                            {!excluido && (
                              <button onClick={() => startEdit(d)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Editar ajuste">
                                <Edit2 size={12} />
                              </button>
                            )}
                            <button
                              onClick={() => toggleExcluir(d)}
                              className={`p-1.5 rounded transition-colors ${excluido ? 'hover:bg-green-100 text-green-600' : 'hover:bg-red-100 text-gray-400 hover:text-red-600'}`}
                              title={excluido ? 'Incluir empleado' : 'Excluir empleado'}
                            >
                              {excluido ? <UserCheck size={12} /> : <UserX size={12} />}
                            </button>
                          </>
                        ))}
                        {esPagado && !excluido && (
                          <button
                            onClick={() => handleDescargarComprobante(d)}
                            disabled={downloadingId === d.id}
                            className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                            title="Descargar comprobante PDF"
                          >
                            {downloadingId === d.id
                              ? <Loader2 size={12} className="animate-spin" />
                              : <Download size={12} />
                            }
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>

          {/* Footer totals */}
          {totalesActivos.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                <td className="px-3 py-2.5 text-gray-700" colSpan={2}>
                  TOTALES ({totalesActivos.length} empleados)
                </td>
                <td className="px-3 py-2.5 text-right text-gray-700">
                  RD$ {fmtShort(totalesActivos.reduce((s, d) => s + parseFloat(d.salario_mensual || 0), 0))}
                </td>
                <td colSpan={2 + (esMixta ? 1 : 0)} />
                {tieneLegal && (
                  <>
                    <td /> {/* Tope vacío */}
                    <td className="px-3 py-2.5 text-right text-blue-700">
                      RD$ {fmt(totalesActivos.reduce((s, d) => s + parseFloat(esMixta ? d.monto_total : d.monto_legal || 0), 0))}
                    </td>
                  </>
                )}
                {tieneAdicional && esDesempeno && (
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {totalesActivos.reduce((s, d) => s + parseFloat(d.puntaje_desempeno || 0), 0).toFixed(0)} pts
                  </td>
                )}
                {tieneAdicional && (
                  <td className="px-3 py-2.5 text-right text-violet-700">
                    RD$ {fmt(totalesActivos.reduce((s, d) => s + parseFloat(d.monto_adicional || 0), 0))}
                  </td>
                )}
                <td className="px-3 py-2.5 text-right text-gray-800">
                  RD$ {fmt(totalesActivos.reduce((s, d) => s + parseFloat(d.monto_total || 0), 0))}
                </td>
                <td />
                <td className="px-3 py-2.5 text-right text-red-600">
                  RD$ {fmt(totalesActivos.reduce((s, d) => s + parseFloat(d.monto_final || 0) * tasaInfotep, 0))}
                </td>
                <td className="px-3 py-2.5 text-right text-orange-600">
                  {bonificacion.isr_calculado
                    ? `RD$ ${fmt(totalesActivos.reduce((s, d) => s + parseFloat(d.isr_monto || 0), 0))}`
                    : '—'
                  }
                </td>
                <td className="px-3 py-2.5 text-right text-green-700">
                  RD$ {fmt(bonificacion.isr_calculado
                    ? totalesActivos.reduce((s, d) => s + parseFloat(d.neto_a_pagar || 0), 0)
                    : totalesActivos.reduce((s, d) => s + parseFloat(d.monto_final || 0) * (1 - tasaInfotep), 0)
                  )}
                </td>
                {(editable || esPagado) && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        total={filtered.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {filtered.length === 0 && (
        <p className="text-center text-sm text-gray-400 py-8">No se encontraron empleados.</p>
      )}

    </div>
  );
}
